import { useTranslation } from '@frontend/i18n';
import { Modal, Text } from '@frontend/design-system';
import { trackingId } from '../../tracking';

/**
 * Modal shown when a user attempts to create an override for a call route that has no location assigned.
 *
 * Overrides require a location to be set on the call route before they can be created. This modal
 * informs the user of that requirement and offers two actions:
 * - **Assign Location** — proceeds to the location assignment flow (`onAssignLocationClick`)
 * - **Cancel** — dismisses the modal without making changes (`onClose`)
 *
 * Used in both the call route index table and the call route details override side panel.
 */
export const CannotCreateOverrideModal = ({
  callRouteName,
  onClose,
  onAssignLocationClick,
}: {
  callRouteName: string;
  onClose: () => void;
  onAssignLocationClick: () => void;
}) => {
  const { t } = useTranslation('phone');

  return (
    <>
      <Modal.Header onClose={onClose}>{t('Cannot Create Override')}</Modal.Header>
      <Modal.Body>
        <Text>
          {t('To create an Override for {{callRouteName}}, you must assign a location to the Call Route.', {
            callRouteName,
          })}
        </Text>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Assign Location'),
          onClick: onAssignLocationClick,
          trackingId: trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'cannot-create-override::assign-location-btn',
          }),
        }}
        secondary={{
          label: t('Cancel'),
          onClick: onClose,
          trackingId: trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'cannot-create-override::cancel-btn',
          }),
        }}
      />
    </>
  );
};
